import { Box, Flex, IconButton, HStack, Button, chakra } from "@chakra-ui/react";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import YouTubePlayer from "./Components/Youtube";
import Music from "./Components/Music";
import { ImageIcon, VideoIcon } from "lucide-react";

const MotionBox = chakra(motion.div);

type ContentCategory = "music" | "video" | "npart";

const Content = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [category, setCategory] = useState<ContentCategory>("music");
  const [mediaType, setMediaType] = useState<"videos" | "images">("videos");
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync tab state if URL parameter is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam === "video" || tabParam === "npart" || tabParam === "music") {
      setCategory(tabParam as ContentCategory);
    }
  }, [location.search]);

  const scrollToTop = () => {
    const mainEl = containerRef.current?.closest("main") || document.querySelector("main");
    if (mainEl) {
      mainEl.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <MotionBox
      ref={containerRef}
      minH="100vh"
      width="100%"
      mx="auto"
      p={{ base: "12px", md: "16px", lg: "20px 24px 120px" }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Box maxWidth="1200px" margin="auto" pb={{ base: "100px", md: "120px" }}>
        {/* Navigation Tab Bar */}
        <Flex justify="center" my={3}>
          <HStack
            spacing={2}
            overflowX="auto"
            py={1}
            px={2}
            bg="white"
            borderRadius="full"
            border="1px solid #E5E8EB"
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.03)"
            css={{
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
            }}
          >
            {/* 1st Tab: Music (노래) */}
            <Button
              size="sm"
              borderRadius="full"
              fontWeight="bold"
              fontSize="13px"
              px={5}
              h="36px"
              bg={category === "music" ? "brand.main" : "transparent"}
              color={category === "music" ? "white" : "#4E5968"}
              _hover={{ bg: category === "music" ? "brand.hover" : "#F2F4F6" }}
              onClick={() => setCategory("music")}
            >
              {t("music")}
            </Button>

            {/* 2nd Tab: Video Content (콘텐츠) */}
            <Button
              size="sm"
              borderRadius="full"
              fontWeight="bold"
              fontSize="13px"
              px={5}
              h="36px"
              bg={category === "video" ? "brand.main" : "transparent"}
              color={category === "video" ? "white" : "#4E5968"}
              _hover={{ bg: category === "video" ? "brand.hover" : "#F2F4F6" }}
              onClick={() => {
                setCategory("video");
                setMediaType("videos");
              }}
            >
              {t("ngorithm")}
            </Button>

            {/* 3rd Tab: N-part (엔퍄트) */}
            <Button
              size="sm"
              borderRadius="full"
              fontWeight="bold"
              fontSize="13px"
              px={5}
              h="36px"
              bg={category === "npart" ? "brand.main" : "transparent"}
              color={category === "npart" ? "white" : "#4E5968"}
              _hover={{ bg: category === "npart" ? "brand.hover" : "#F2F4F6" }}
              onClick={() => setCategory("npart")}
            >
              {t("npart")}
            </Button>
          </HStack>
        </Flex>

        {category === "npart" && (
          <MotionBox
            display="flex"
            justifyContent="center"
            initial={{ opacity: 0, y: -1 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -1 }}
            mb={3}
          >
            <IconButton
              aria-label="Videos"
              icon={<VideoIcon size={18} />}
              bg={mediaType === "videos" ? "brand.main" : "gray.100"}
              color={mediaType === "videos" ? "white" : "brand.main"}
              borderRadius="full"
              size="md"
              mr={3}
              _hover={{
                bg: mediaType === "videos" ? "brand.hover" : "gray.200",
              }}
              onClick={() => setMediaType("videos")}
            />
            <IconButton
              aria-label="Images"
              icon={<ImageIcon size={18} />}
              bg={mediaType === "images" ? "brand.main" : "gray.100"}
              color={mediaType === "images" ? "white" : "brand.main"}
              borderRadius="full"
              size="md"
              _hover={{
                bg: mediaType === "images" ? "brand.hover" : "gray.200",
              }}
              onClick={() => setMediaType("images")}
            />
          </MotionBox>
        )}

        {/* Feature Render */}
        {category === "music" && <Music />}
        {(category === "video" || category === "npart") && (
          <YouTubePlayer
            category={category}
            mediaType={mediaType}
            scrollToTop={scrollToTop}
          />
        )}
      </Box>
    </MotionBox>
  );
};

export default Content;